class NewsletterPopup extends ModalDialog {
  constructor() {
    super();
    this.closed = getCookie("newsletter-closed");
    this.subscribed = getCookie("newsletter-subscribed");
    this.form = this.querySelector(".js-form");
    this.floatingButton = this.querySelector(".newsletter-floating-button");
    this.floatingButtonClose = this.querySelector(
      ".newsletter-floating-button__close"
    );

    if (this.form) {
      this.form.addEventListener("submit", this.onSubscribe.bind(this));
      this.initCaptcha();
    }

    // Only an explicit "no thanks" dismisses the floating button for the
    // session. Closing the dialog via escape or the backdrop keeps it around.
    this.querySelector('[id^="ModalClose-"]').addEventListener("click", () => {
      this.dismissFloatingButton();
    });

    if (this.floatingButton) {
      this.floatingButton.addEventListener("click", (e) => {
        if (e.target.closest(".newsletter-floating-button__close")) {
          e.stopPropagation();
          this.dismissFloatingButton();
          return;
        }
        this.show();
      });

      this.initSwipeToDismiss();
    }
  }

  initSwipeToDismiss() {
    let startX = 0;
    let currentX = 0;
    let swiping = false;

    this.floatingButton.addEventListener("touchstart", (e) => {
      startX = e.touches[0].clientX;
      currentX = startX;
      swiping = true;
      this.floatingButton.style.transition = "none";
    }, { passive: true });

    this.floatingButton.addEventListener("touchmove", (e) => {
      if (!swiping) return;
      currentX = e.touches[0].clientX;
      const diff = currentX - startX;
      if (diff > 0) {
        this.floatingButton.style.transform = `translateX(${diff}px)`;
        this.floatingButton.style.opacity = Math.max(0, 1 - diff / 150);
      }
    }, { passive: true });

    this.floatingButton.addEventListener("touchend", () => {
      if (!swiping) return;
      swiping = false;
      const diff = currentX - startX;
      this.floatingButton.style.transition = "";

      if (diff > 80) {
        this.floatingButton.style.transform = "translateX(200%)";
        this.floatingButton.style.opacity = "0";
        setTimeout(() => this.dismissFloatingButton(), 300);
      } else {
        this.floatingButton.style.transform = "";
        this.floatingButton.style.opacity = "";
      }
    });
  }

  connectedCallback() {
    if (Shopify.designMode) {
      if (this.dataset.openInDesignMode === "true") {
        this.show();
      }
      return;
    }

    if (this.closed !== null || this.subscribed !== null) {
      this.hideFloatingButton();
      return;
    }

    // Check if quiz is present and not yet completed
    var quizPending = document.querySelector("style-quiz") && !getCookie("style-quiz-completed");

    if (quizPending) {
      // Hide button until quiz is dismissed
      this.hideFloatingButton();
      document.addEventListener("style-quiz:dismissed", () => {
        setTimeout(() => this.showFloatingButton(), 3000);
      }, { once: true });
    }
  }

  showFloatingButton() {
    if (this.closed !== null || this.subscribed !== null) return;
    if (this.floatingButton) {
      this.floatingButton.classList.remove("hidden");
    }
  }

  hideFloatingButton() {
    if (this.floatingButton) {
      this.floatingButton.classList.add("hidden");
    }
  }

  dismissFloatingButton() {
    setCookie("newsletter-closed", "true");
    this.hideFloatingButton();
  }

  // When the store's hCaptcha protection is on, Shopify's script intercepts
  // customer-form submits, fetches a token and then calls form.submit()
  // natively — reloading the page before the discount code can be read.
  // It captures form.submit at bind time, so pointing it at our background
  // submit first makes the captcha flow end in our fetch (with the token
  // already injected as a hidden input) instead of a navigation.
  initCaptcha() {
    if (!window.Shopify?.captcha?.protect) return;

    this.captchaBound = true;
    this.form.submit = () => this.submitForm();
    Shopify.captcha.protect(this.form);
  }

  onSubscribe(event) {
    event.preventDefault();

    // With captcha active, its own submit listener runs the widget and then
    // calls the patched form.submit() above once the token is in the form.
    if (this.captchaBound) return;

    this.submitForm();
  }

  // Submit in the background so the popup stays open and can show the
  // discount code. A page reload would take it away before it is read.
  async submitForm() {
    if (this.submitting) return;
    this.submitting = true;
    this.setSubmitting(true);

    try {
      const response = await fetch(this.form.action, {
        method: "POST",
        body: new FormData(this.form),
        headers: { Accept: "text/html" }
      });
      const html = await response.text();

      const error = this.getFormError(html);
      if (error) {
        this.showError(error);
        return;
      }

      this.showSubscribed();
    } catch (e) {
      // Network failure — fall back to the native submit so the customer
      // still gets subscribed, even though they lose the popup. form.submit
      // may be patched above, so go through the prototype.
      HTMLFormElement.prototype.submit.call(this.form);
    } finally {
      this.submitting = false;
      this.setSubmitting(false);
    }
  }

  // Shopify re-renders the page with form.errors on failure, so read the
  // error straight out of this form's own markup in the response.
  getFormError(html) {
    const doc = new DOMParser().parseFromString(html, "text/html");
    const form = doc.getElementById(this.form.id);
    const message = form?.querySelector(".field.has-error .field__message");
    return message?.textContent.trim() || null;
  }

  showError(message) {
    const field = this.form.querySelector("[data-input-wrapper]");
    const messageEl = field?.querySelector("[data-message]");
    if (!field || !messageEl) return;

    field.classList.add("has-error");
    messageEl.classList.remove("hidden");
    messageEl.textContent = message;
  }

  setSubmitting(isSubmitting) {
    const button = this.form.querySelector('[type="submit"]');
    if (!button) return;

    button.disabled = isSubmitting;
    button.setAttribute("aria-busy", isSubmitting);
  }

  showSubscribed() {
    setCookie("newsletter-subscribed", "true", 365);
    this.form.classList.add("hidden");

    const preSubscriptionContent = this.querySelector(
      ".newsletter-popup__pre-subscription"
    );
    const postSubscriptionContent = this.querySelector(
      ".newsletter-popup__post-subscription"
    );

    if (preSubscriptionContent && postSubscriptionContent) {
      preSubscriptionContent.classList.add("hidden");
      postSubscriptionContent.classList.remove("hidden");
    }

    this.hideFloatingButton();
  }
}

customElements.define("newsletter-popup", NewsletterPopup);
